import React from 'react';
import CurriculumSupportIcon from 'Views/Components/Icons/CurriculumSupportIcon';
import StudentAdvisorIcon from 'Views/Components/Icons/StudentAdvisorIcon';
import { ICollectionHeaderProps } from 'Views/Components/Collection/CollectionHeaders';
import { StudyPlanEntity } from 'Models/Entities';
import StatusChip from 'Views/Components/Chip/ProgramVersionStatusChip';
import { programStatusOptions } from 'Models/Enums';
import Toggle from 'Views/Components/Toggle/Toggle';
import alertToast from 'Util/ToastifyUtils';
import moment from 'moment';
import { Conditions } from 'Util/QueryHelpers';
import { serialiseDateTimeUTC } from 'Util/AttributeUtils';
import { IStudyPlanFilterState } from 'Views/Components/StudyPlanCollection/StudyPlanCollectionSearchFilter';
import { SelectOption } from 'Views/Components/Select/Select';
import _ from 'lodash';
import { toLower } from 'lodash';

export const mobileCollectionHeaders:Array<ICollectionHeaderProps<StudyPlanEntity>> = [
	{
		name: 'name',
		displayName: 'Name',
	},
];

export const availableDesktopHeaders:Array<ICollectionHeaderProps<StudyPlanEntity>> = [
	...mobileCollectionHeaders,
	{
		name: 'programCode',
		displayName: `${window.lexicon.program} ${toLower(window.lexicon.code)}`,
		transformItem: (item: StudyPlanEntity) => item.programVersion?.program?.code,
	},
	{
		name: 'programName',
		displayName: `${window.lexicon.program} ${toLower(window.lexicon.title)}`,
		transformItem: (item: StudyPlanEntity) => item.programVersion?.program?.displayName,
	},
	{
		name: 'year',
		displayName: window.lexicon.commencingYear,
		transformItem: (item: StudyPlanEntity) => item.programVersion?.getDisplayName(),
	},
	{
		name: 'programVersionStatus',
		displayName: 'Version status',
		transformItem: (item: StudyPlanEntity) => (
			<StatusChip status={programStatusOptions[item.programVersion?.status]} />
		),
	},
	{
		name: 'lastModified',
		displayName: 'Last modified',
		transformItem: (item: StudyPlanEntity) => moment(item.modified).format('DD/MM/YYYY'),
	},
	{
		name: 'modifiedBy',
		displayName: 'Modified by',
		transformItem: (item: StudyPlanEntity) => getUserIcon(item.modifiedByUserName, item.modifiedByUserType),
	},
	{
		name: 'createdBy',
		displayName: 'Created by',
		transformItem: (item: StudyPlanEntity) => getUserIcon(item.ownerName, item.ownerUserType),
	},
	{
		name: 'active',
		displayName: 'Active',
		ignoreOnRowClick: true,
		transformItem: () => (
			<Toggle
				ariaLabel="active state toggle"
				onLabel="Yes"
				offLabel="No"
				onChange={() => alertToast('TODO: #341 Milestone 11')}
			/>
		),
	},
];

export const getUserIcon = (userName?: string, userType?: string) => {
	const renderIcon = () => {
		if (!userName) {
			return '-';
		}

		switch (userType) {
			case 'UserEntity':
				return (
					<span
						className="icon icon-right icon-school"
						data-tooltip-id="role-tooltip"
						data-tooltip-content="Student"
						data-tooltip-place="top"
						aria-label={`${userName} - Student`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
					</span>
				);
			case 'CurriculumSupportEntity':
				return (
					<span
						className="icon icon-right"
						data-tooltip-id="role-tooltip"
						data-tooltip-content={window.lexicon.curriculumSupport}
						data-tooltip-place="top"
						aria-label={`${userName} - ${window.lexicon.curriculumSupport}`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
						<CurriculumSupportIcon />
					</span>
				);
			case 'InstitutionAdminEntity':
				return (
					<span
						className="icon icon-right icon-shield-person"
						data-tooltip-id="role-tooltip"
						data-tooltip-content={window.lexicon.institutionAdmin}
						data-tooltip-place="top"
						aria-label={`${userName} - ${window.lexicon.institutionAdmin}`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
					</span>
				);
			case 'StudentAdvisorEntity':
				return (
					<span
						className="icon icon-right"
						data-tooltip-id="role-tooltip"
						data-tooltip-content={window.lexicon.academicAdvisor}
						data-tooltip-place="top"
						aria-label={`${userName} - ${window.lexicon.academicAdvisor}`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
						<StudentAdvisorIcon />
					</span>
				);
			case 'AdminEntity':
				return (
					<span
						className={'icon icon-right icon-manage_accounts'}
						data-tooltip-id="role-tooltip"
						data-tooltip-content={window.lexicon.administrator}
						data-tooltip-place="top"
						aria-label={`${userName} - ${window.lexicon.administrator}`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
					</span>
				);
			case 'User':
				return (
					<span
						className={'icon icon-right icon-supervisor-account'}
						data-tooltip-id="role-tooltip"
						data-tooltip-content="Super User"
						data-tooltip-place="top"
						aria-label={`${userName} - Supe User`}
						tabIndex={0}
						role="button"
						aria-roledescription="tool tip"
					>
						{userName}
					</span>
				);
			default:
				return '-';
		}
	};

	return (
		<div className="study-plan-user">
			{renderIcon()}
		</div>
	);
};

export const getFilterPlansGraphQlConditions = (filterValues: IStudyPlanFilterState): Conditions => {
	const newSearchParams: Conditions = [];

	if (filterValues.lastModifiedAfter) {
		newSearchParams.push(
			[
				{
					path: 'modified',
					comparison: 'greaterThanOrEqual',
					value: [serialiseDateTimeUTC(filterValues.lastModifiedAfter)],
				},
			],
		);
	}

	if (filterValues.lastModifiedBefore) {
		newSearchParams.push(
			[
				{
					path: 'modified',
					comparison: 'lessThanOrEqual',
					value: [serialiseDateTimeUTC(filterValues.lastModifiedBefore)],
				},
			],
		);
	}

	if (filterValues.programCode) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.program.code',
					comparison: 'equal',
					value: [filterValues.programCode],
				},
			],
		);
	}

	if (filterValues.programTitle) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.program.title',
					comparison: 'equal',
					value: [filterValues.programTitle],
				},
			],
		);
	}

	if (filterValues.commencingYear) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.commencingYear',
					comparison: 'equal',
					value: [filterValues.commencingYear],
				},
			],
		);
	}

	if (filterValues.versionStatus) {
		newSearchParams.push(
			[
				{
					path: 'programVersion.status',
					comparison: 'equal',
					value: [filterValues.versionStatus],
				},
			],
		);
	}

	if (filterValues.createdBy) {
		newSearchParams.push(
			[
				{
					path: 'owner',
					comparison: 'equal',
					value: [filterValues.createdBy],
				},
			],
		);
	}

	if (filterValues.modifiedBy) {
		newSearchParams.push(
			[
				{
					path: 'modifiedBy',
					comparison: 'equal',
					value: [filterValues.modifiedBy],
				},
			],
		);
	}

	return newSearchParams;
};

export const sortByUniqueAscending = (
	options: SelectOption<string>[],
) => (
	_.uniqBy(options, 'value')
		.filter(option => typeof option.display === 'string')
		.sort(
			(a, b) => {
				if (typeof a.display !== 'string' || typeof b.display !== 'string') {
					throw new Error('Cannot sort on non-string values');
				}

				return a.display.localeCompare(b.display);
			},
		)
);
